import { Link } from 'react-router-dom';
import type { Comic } from '@/types';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  const formatViews = (views: number) => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-16 h-[88px] flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            title={`${comic.title} - 韩漫画网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span className="flex items-center gap-0.5">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {comic.rating}
            </span>
            <span>{comic.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group cursor-pointer"
    >
      <div className="relative rounded-lg overflow-hidden aspect-[3/4] mb-3">
        <img
          src={comic.cover}
          alt={comic.title}
          title={`${comic.title} - 韩漫画网`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <span className="text-white text-xs font-medium line-clamp-2">{comic.description}</span>
        </div>
        <div className="absolute top-2 left-2">
          {comic.status === '已完结' && (
            <span className="px-2 py-0.5 bg-foreground-900/80 text-background-50 text-xs rounded-md font-medium">
              已完结
            </span>
          )}
          {comic.rating >= 9.5 && (
            <span className="px-2 py-0.5 bg-primary-500/90 text-background-50 text-xs rounded-md font-medium ml-1">
              高分
            </span>
          )}
        </div>
      </div>
      <div>
        <div className="mb-1.5">
          {comic.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-md font-medium mr-1">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center justify-between mt-1.5 text-xs text-foreground-400">
          <div className="flex items-center gap-0.5">
            <i className="ri-star-fill text-accent-500"></i>
            <span className="font-medium text-foreground-700">{comic.rating}</span>
          </div>
          <span>{formatViews(comic.views)} 阅读</span>
        </div>
      </div>
    </Link>
  );
}